import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Send,
  Mail,
  Phone,
  MapPin,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import {
  validateFormData,
  sendEmail,
  initEmailService,
} from "../../utils/emailService";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [formState, setFormState] = useState({
    isSubmitting: false,
    isSubmitted: false,
    isError: false,
    errors: {},
    submitMessage: "",
  });

  // Inicializar servicio de email
  useEffect(() => {
    // Descomentar cuando hayas configurado tu cuenta EmailJS
    // initEmailService();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Limpiar mensaje de error para este campo si existe
    if (formState.errors[name]) {
      setFormState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          [name]: "",
        },
      }));
    }
  };

  // --------------------------------------------------------------------------
  // handleSubmit: valida y abre WhatsApp con mensaje prellenado
  // --------------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar datos del formulario
    const { isValid, errors } = validateFormData(formData);

    if (!isValid) {
      setFormState((prev) => ({
        ...prev,
        errors,
        isError: true,
        submitMessage: "Por favor corrige los errores en el formulario.",
      }));
      return;
    }

    setFormState((prev) => ({
      ...prev,
      isSubmitting: true,
      isError: false,
      errors: {},
      submitMessage: "",
    }));

    try {
      // ---- CONFIGURA AQUÍ TU NÚMERO DE WHATSAPP (formato internacional sin + ni espacios) ----
      const WHATSAPP_NUMBER = "+525551055881"; // ejemplo: 5491123456789
      // ---------------------------------------------------------------------------------------

      // Construir texto a enviar
      const textLines = [
        `Hola, soy ${formData.name} (${formData.email}).`,
        `Asunto: ${formData.subject}`,
        "",
        formData.message,
      ];
      const text = textLines.join("\n");

      // URL de WhatsApp (wa.me)
      const phone = WHATSAPP_NUMBER.replace(/\D/g, "");
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;

      // Abrir en nueva pestaña/ventana (web o app en móvil)
      window.open(waUrl, "_blank");

      // Actualizar estado (simular éxito)
      setFormState({
        isSubmitting: false,
        isSubmitted: true,
        isError: false,
        errors: {},
        submitMessage:
          "Se abrió WhatsApp con tu mensaje. Completa y envía desde allí.",
      });

      // Opcional: limpiar formulario
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });

      // Resetear notificación después de 5s
      setTimeout(() => {
        setFormState((prev) => ({
          ...prev,
          isSubmitted: false,
          submitMessage: "",
        }));
      }, 5000);
    } catch (error) {
      console.error("Error al preparar WhatsApp:", error);
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        isError: true,
        errors: {},
        submitMessage:
          "Hubo un problema al abrir WhatsApp. Por favor intenta nuevamente.",
      });
    }
  };
  // --------------------------------------------------------------------------

  const contactInfo = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      value: "rosalba.olivieri1@gmail.com",
      link: "mailto:rosalba.olivieri1@gmail.com",
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Teléfono",
      value: "+525551055881",
      link: "tel:+525551055881",
    },
  ];

  return (
    <section id="contact" className="py-20 bg-primary-dark">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-accent-light">
            Contacto
          </h2>
          <p className="text-lg text-accent-light text-opacity-80">
            ¿Interesado en adquirir una obra o tienes alguna consulta? Ponte en
            contacto y responderé a la brevedad.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-12">
          <motion.div
            className="md:col-span-5"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h3 className="text-2xl font-bold mb-8 text-accent-light">
              Información de Contacto
            </h3>

            <div className="space-y-8">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent flex items-center justify-center text-primary-dark">
                    {info.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-accent-light">
                      {info.title}
                    </h4>
                    <a
                      href={info.link}
                      className="text-accent-light text-opacity-80 hover:text-accent transition-colors duration-300"
                    >
                      {info.value}
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Información de privacidad */}
            <motion.div
              className="mt-12 bg-primary-light bg-opacity-40 p-6 rounded-lg border border-accent border-opacity-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h4 className="text-accent font-semibold mb-2 flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                Política de privacidad
              </h4>
              <p className="text-sm text-accent-light text-opacity-70">
                Tu información personal será utilizada exclusivamente para
                responder a tus consultas y no será compartida con terceros.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            className="md:col-span-7"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="bg-primary-light rounded-2xl p-8 shadow-xl border border-accent border-opacity-20">
              <h3 className="text-2xl font-bold mb-6 text-accent-light">
                Envíame un Mensaje
              </h3>

              {/* Mensaje de éxito */}
              {formState.isSubmitted && (
                <motion.div
                  className="p-6 bg-green-50 text-green-800 rounded-xl flex items-center gap-3 mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                  <p className="font-medium">{formState.submitMessage}</p>
                </motion.div>
              )}

              {/* Mensaje de error */}
              {formState.isError && formState.submitMessage && (
                <motion.div
                  className="p-6 bg-red-50 text-red-800 rounded-xl flex items-center gap-3 mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                  </div>
                  <p className="font-medium">{formState.submitMessage}</p>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block mb-2 text-sm font-medium text-accent-light"
                    >
                      Nombre <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`w-full bg-primary-lighter border ${
                        formState.errors.name
                          ? "border-red-500"
                          : "border-accent border-opacity-30"
                      } rounded-lg px-4 py-3 text-accent-light placeholder-accent placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-accent`}
                      placeholder="Tu nombre"
                    />
                    {formState.errors.name && (
                      <p className="mt-1 text-red-500 text-xs">
                        {formState.errors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block mb-2 text-sm font-medium text-accent-light"
                    >
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={`w-full bg-primary-lighter border ${
                        formState.errors.email
                          ? "border-red-500"
                          : "border-accent border-opacity-30"
                      } rounded-lg px-4 py-3 text-accent-light placeholder-accent placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-accent`}
                      placeholder="tu@email.com"
                    />
                    {formState.errors.email && (
                      <p className="mt-1 text-red-500 text-xs">
                        {formState.errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="subject"
                    className="block mb-2 text-sm font-medium text-accent-light"
                  >
                    Asunto <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className={`w-full bg-primary-lighter border ${
                      formState.errors.subject
                        ? "border-red-500"
                        : "border-accent border-opacity-30"
                    } rounded-lg px-4 py-3 text-accent-light placeholder-accent placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder="¿Cómo puedo ayudarte?"
                  />
                  {formState.errors.subject && (
                    <p className="mt-1 text-red-500 text-xs">
                      {formState.errors.subject}
                    </p>
                  )}
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="message"
                    className="block mb-2 text-sm font-medium text-accent-light"
                  >
                    Mensaje <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className={`w-full bg-primary-lighter border ${
                      formState.errors.message
                        ? "border-red-500"
                        : "border-accent border-opacity-30"
                    } rounded-lg px-4 py-3 text-accent-light placeholder-accent placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder="Escribe tu mensaje aquí..."
                  ></textarea>
                  {formState.errors.message && (
                    <p className="mt-1 text-red-500 text-xs">
                      {formState.errors.message}
                    </p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  className="bg-accent hover:bg-accent-light text-primary-dark py-3 px-6 rounded-lg font-semibold shadow-lg flex items-center justify-center gap-2 w-full sm:w-auto"
                  disabled={formState.isSubmitting}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {formState.isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-dark"></div>
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Enviar Mensaje</span>
                    </>
                  )}
                </motion.button>
              </form>

              {/* Nota sobre campos requeridos */}
              <div className="mt-4 text-xs text-accent-light text-opacity-60">
                Los campos marcados con <span className="text-red-500">*</span>{" "}
                son obligatorios.
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
